export type E164PhoneNumber = `+${number}`;
export type ShortCode = `${number}`;
export type AlphanumericSenderId = string;

/** US common short codes are 5 or 6 digits, with no country code. */
export const isShortCode = (value: string): value is ShortCode =>
  /^\d{5,6}$/.test(value);

const USA_COUNTRY_CODE = "+1";

// NOTE: only supports US numbers — assumes +1 and a 10-digit national number.
export function convertArbitraryPhoneNumberToE164(
  phoneNumber: string,
): E164PhoneNumber | null {
  const sanitizedPhoneNumber = phoneNumber.replace(/[^\d+]/g, "");
  if (sanitizedPhoneNumber.startsWith("+")) {
    return isValidE164PhoneNumber(sanitizedPhoneNumber)
      ? sanitizedPhoneNumber
      : null;
  } else if (
    sanitizedPhoneNumber.length === 11 &&
    sanitizedPhoneNumber.startsWith("1")
  ) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    return `+${sanitizedPhoneNumber}` as E164PhoneNumber;
  } else if (
    sanitizedPhoneNumber.length >= 10 &&
    sanitizedPhoneNumber.length <= 14
  ) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    return `${USA_COUNTRY_CODE}${sanitizedPhoneNumber}` as E164PhoneNumber;
  }
  return null;
}

export function isValidE164PhoneNumber(
  phoneNumber: string,
): phoneNumber is E164PhoneNumber {
  return /^\+[1-9]\d{7,14}$/.test(phoneNumber);
}

/**
 * Converts a masked US phone input value (e.g. `+1 (312) 555-0123`) to E.164.
 * US numbers are complete at 11 digits: the country code plus 10.
 */
export function e164FromMaskedUSPhoneNumber(
  value: string,
): E164PhoneNumber | undefined {
  const digits = value.replace(/\D/g, "");
  if (digits.length !== 11 || !digits.startsWith("1")) {
    return undefined;
  }
  const candidate = `+${digits}`;
  return isValidE164PhoneNumber(candidate) ? candidate : undefined;
}

/**
 * A cleared masked US phone input may still hold mask literals (`+1 (`), so
 * emptiness is judged on digits alone.
 */
export function maskedUSPhoneNumberIsEmpty(value: string): boolean {
  const digits = value.replace(/\D/g, "");
  return digits === "" || digits === "1";
}

const BOGUS_PHONE_NUMBERS: ReadonlySet<string> = new Set([
  "+11234567890",
  "+11123456789",
]);

export function getE164PhoneNumber(
  phoneNumber?: string,
): E164PhoneNumber | undefined {
  const e164 = phoneNumber
    ? (convertArbitraryPhoneNumberToE164(phoneNumber) ?? undefined)
    : undefined;
  if (e164 && BOGUS_PHONE_NUMBERS.has(e164)) {
    return undefined;
  }
  return e164;
}

export function createSecureSmsUri(params: {
  phoneNumbers?: string[];
  body?: string;
}): string | null {
  const { phoneNumbers, body } = params;

  // Validate and sanitize phone numbers
  const validatedNumbers: string[] = [];
  if (phoneNumbers && phoneNumbers.length > 0) {
    for (const number of phoneNumbers) {
      if (!number || typeof number !== "string") {
        continue;
      }

      // Remove any potentially dangerous characters and validate
      const sanitizedNumber = number.replace(/[^\d+,]/g, "");

      // Check if it's a valid phone number format
      // Allow formats like +1234567890, 1234567890, 12345, etc.
      if (
        /^[\d+,]+$/.test(sanitizedNumber) &&
        (isShortCode(sanitizedNumber) ||
          (sanitizedNumber.length >= 10 && sanitizedNumber.length <= 20))
      ) {
        validatedNumbers.push(sanitizedNumber);
      }
    }

    if (validatedNumbers.length === 0) {
      return null;
    }
  }

  // Sanitize body text - ensure it's properly encoded and safe
  let encodedBody = "";
  if (body && typeof body === "string") {
    // Remove control characters except line breaks, and ensure proper encoding
    const sanitizedBody = body
      .replace(/[\x00-\x09\x0B-\x1F\x7F]/g, "")
      .substring(0, 1000); // Limit length to prevent abuse

    encodedBody = encodeURIComponent(sanitizedBody);
  }

  // Build the SMS URI with platform-specific formatting
  const numbersString = validatedNumbers.join(",");

  if (encodedBody) {
    // Detect iOS for proper SMS URI formatting
    const isIOS =
      typeof navigator !== "undefined" &&
      /iPhone|iPad|iPod/.test(navigator.userAgent) &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      ("ontouchend" in document || (navigator as any).maxTouchPoints > 1);

    // iOS uses & separator, other platforms use ?
    const separator = isIOS ? "&" : "?";
    return `sms:${numbersString}${separator}body=${encodedBody}`;
  }

  return `sms:${numbersString}`;
}
